import {
	CheckinOrderFragment,
	CheckinSaveOrderServicesMutationVariables,
	CheckoutOrderFragment,
	SaveOrderServicesMutationVariables
} from '@websky/graphql';
import { useMemo } from 'react';
import { State, TransferInfoBySegment } from './types';
import { isCheckoutOrderGuard } from '../../typeguards';
import { UseRefundSummaryParams } from '../../RefundSummary/hooks';
import { UpgradeFlightClassOptions } from '../hooks/UpgradeFlightClass.hook';
import { isCheckinFlightGuard } from './utils';

export const useSelectors = (
	state: State,
	order: CheckoutOrderFragment | CheckinOrderFragment,
	onContinueWithoutSelection: UpgradeFlightClassOptions['onContinueWithoutSelection']
) => {
	const saveOrderServicesVariables = useMemo<
		SaveOrderServicesMutationVariables | CheckinSaveOrderServicesMutationVariables
	>(() => {
		const service = state.service;
		const travellers = state.travellersIds;

		const variables: SaveOrderServicesMutationVariables | CheckinSaveOrderServicesMutationVariables = {
			params: {
				id: state.orderId,
				services: travellers.map(travellerId => ({
					travellerId: travellerId,
					gdsServices: {
						addServices: state.selectedSegments.map(segmentId => ({
							segmentIds: [segmentId],
							count: 1,
							serviceId: service.id
						}))
					}
				}))
			}
		};

		return variables;
	}, [state.selectedSegments]);

	const transferDurationBySegments = useMemo<TransferInfoBySegment>(() => {
		const transferDurationBySegmentId = {};
		if (isCheckoutOrderGuard(order)) {
			for (const segment of order.flight.segments) {
				transferDurationBySegmentId[segment.segment.id] = segment.transferDuration;
			}

			return transferDurationBySegmentId;
		} else {
			return {};
		}
	}, [order]);

	const refundSummaryParams: UseRefundSummaryParams = useMemo<UseRefundSummaryParams>(() => {
		return {
			orderId: order.id,
			passengers: order.travellers as UseRefundSummaryParams['passengers'],
			selectedPassengerIds: state.service.allowedPassengers.map(group => group[0]),
			selectedSegmentIds: state.selectedSegments,
			allFlightToExchange: isCheckoutOrderGuard(order)
				? order.flight.segments.length === state.selectedSegments.length
				: order.segments.length === state.selectedSegments.length
		};
	}, [order, state]);

	const nextButtonDisabled: boolean = useMemo<boolean>(() => {
		if (onContinueWithoutSelection) {
			return false;
		}

		return state.selectedSegments.length === 0;
	}, [onContinueWithoutSelection, state.selectedSegments]);

	const isAllowInstantSelect = useMemo<boolean>(() => {
		return !isCheckinFlightGuard(state.flights[0])
			? state.flights.length === 1 && state.flights[0].segments?.length === 1
			: false;
	}, [order]);

	return {
		transferDurationBySegments,
		saveOrderServicesVariables,
		refundSummaryParams,
		nextButtonDisabled,
		isAllowInstantSelect
	};
};
